import React, { FC, useMemo } from "react";
import styled from "styled-components";
import { useToasts } from "react-toast-notifications";
import { ButtonProps, Theme } from "@literati/public-ui-shared-react";
import { isAPIError } from "lib/api/base";
import { postBookShelfQuizRecommendationPreferences } from "lib/api/bookShelfQuiz";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import BookCheckoutLineItem from "components/bookCheckout/BookCheckoutLineItem";
import { LineItems } from "components/bookCheckout/bookCheckoutStyles";
import {
  MembershipState,
  MembershipStep,
  SelectBooksSubmit,
  SelectBooksValues,
  SelectedBookKey,
} from "components/Membership/membershipTypes";

const StyledStepLayout = styled(StepLayout)`
  .${StepLayoutClassName.FORM} {
    max-width: 25.25rem;
  }
`;

const StyledLineItems = styled(LineItems)`
  margin: 2rem 0 0;
`;

export const MembershipStepSelectBooks = ({
  onSubmit,
  quizId,
  recommendations,
  router,
  selectBooks,
}: MembershipState) => {
  const { addToast } = useToasts();

  const recommendationValues = useMemo(() => {
    const data: { [key: SelectedBookKey]: boolean } = {};
    recommendations?.reduce(
      (obj, rec) => ((obj[`book-${rec.book?.id}`] = true), obj),
      data
    );
    return data;
  }, [recommendations]);

  const handleSubmit: SelectBooksSubmit = async (values) => {
    const { kind: _, ...recs } = values;

    const recValues = Object.values(recs);
    const bookPreferences = Object.keys(recs).map((val, idx) => {
      const bookId = Number(val.split("-")?.[1]);
      const preferenceValue = recValues?.[idx] ? 2 : 0;
      return { bookId, preferenceValue };
    });

    const res = await postBookShelfQuizRecommendationPreferences({
      bookPreferences,
      quizId: quizId || "",
    });

    if (isAPIError(res)) {
      addToast(
        res.error.message || "There was a problem submitting your preferences",
        {
          appearance: "error",
        }
      );
    }

    return onSubmit?.(values);
  };

  const backButtonProps: ButtonProps = {
    onClick: () => router?.push(`/quiz/results/?quizId=${quizId}`),
  };

  return (
    <Theme>
      <StyledStepLayout<FC<StepLayoutProps<SelectBooksValues>>>
        asForm
        bgColor="white"
        formId="membership-step-select-books"
        formikProps={{
          initialValues: {
            kind: "select-books",
            ...recommendationValues,
            ...selectBooks,
          },
          onSubmit: handleSubmit,
        }}
        heading="Bookshelf Selections"
        stepFooterProps={{
          backButtonProps,
        }}
        stepHeaderProps={{
          backButtonProps,
        }}
        subheading="
          Interested in getting any of your recommended titles in your 
          Literati Book Club box? Select them below and we’ll try to add 
          them—or closely related titles—to one of your boxes.
        "
      >
        {({ values }) => {
          return (
            <StyledLineItems>
              {recommendations?.map((rec) => (
                <BookCheckoutLineItem
                  book={rec.book}
                  checkedBgColor="periwinkleTint5"
                  key={rec.book?.id}
                  checked={values[`book-${rec.book?.id}`]}
                />
              ))}
            </StyledLineItems>
          );
        }}
      </StyledStepLayout>
    </Theme>
  );
};

export const MEMBERSHIP_STEP_SELECT_BOOKS: MembershipStep = {
  component: MembershipStepSelectBooks,
  id: "membership-id-select-books",
  slug: "select-books",
};

export default MembershipStepSelectBooks;
